import React, { useCallback, useEffect, useState, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import './YouTubeTestimonials.scss';

// Declare global YT interface for TypeScript-like behavior
const loadYouTubeAPI = () => {
  return new Promise((resolve) => {
    if (window.YT && window.YT.Player) {
      resolve(window.YT);
      return;
    }

    window.onYouTubeIframeAPIReady = () => {
      resolve(window.YT);
    };

    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }
  });
};

const YouTubeTestimonials = () => {
  const testimonials = [
    {
      id: 1,
      videoId: "amo_RnMSuKY",
      title: "Patient Success Story - Diabetes Management"
    },
    {
      id: 2,
      videoId: "2OPJaJoi0Gs",
      title: "Virtual Consultation Experience"
    },
    {
      id: 3,
      videoId: "XalSjho8J5Q",
      title: "Family Healthcare Journey"
    },
    {
      id: 4,
      videoId: "AQmvWNxE-XM",
      title: "Corporate Health Program Success"
    },
    {
      id: 5,
      videoId: "xaVclNCE4BY",
      title: "Specialist Access Made Easy"
    },
    {
      id: 6,
      videoId: "qrCEQBEdrC4",
      title: "Remote Monitoring Benefits"
    }
  ];

  // Hold references to all YouTube players
  const playersRef = useRef([]);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      align: 'start',
      slidesToScroll: 1,
      skipSnaps: false,
      dragFree: false,
      containScroll: 'trimSnaps',
      breakpoints: {
        '(max-width: 768px)': { 
          slidesToScroll: 1,
          align: 'center',
          containScroll: 'keepSnaps'
        },
        '(min-width: 769px)': {
          slidesToScroll: 1,
          align: 'start'
        }
      }
    },
    [Autoplay({ 
      delay: 5000, 
      stopOnInteraction: false,
      stopOnMouseEnter: true,
      playOnInit: true
    })]
  );

  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback((emblaApi) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);
  }, [emblaApi, onSelect]);

  // Initialize YouTube players
  useEffect(() => {
    const initPlayers = async () => {
      try {
        await loadYouTubeAPI();
        
        testimonials.forEach((testimonial) => {
          const playerId = `youtube-player-${testimonial.id}`;
          const playerElement = document.getElementById(playerId);
          
          // Skip if already initialized or element doesn't exist
          if (!playerElement || playerElement.dataset.initialized === 'true') {
            return;
          }

          const player = new window.YT.Player(playerId, {
            videoId: testimonial.videoId,
            playerVars: {
              rel: 0,
              modestbranding: 1,
              showinfo: 0,
              controls: 1,
              enablejsapi: 1,
              origin: window.location.origin
            },
            events: {
              onStateChange: (event) => {
                // When a video starts playing, pause all other videos
                if (event.data === window.YT.PlayerState.PLAYING) {
                  playersRef.current.forEach((otherPlayer) => {
                    if (otherPlayer !== event.target) {
                      try {
                        otherPlayer.pauseVideo();
                      } catch (error) {
                        console.warn('Error pausing video:', error);
                      }
                    }
                  });
                }
              },
              onReady: () => {
                // Player is ready, add to our reference array
                if (!playersRef.current.includes(player)) {
                  playersRef.current.push(player);
                }
              }
            }
          });

          // Mark as initialized
          playerElement.dataset.initialized = 'true';
        });
      } catch (error) {
        console.error('Error initializing YouTube players:', error);
      }
    };

    // Small delay to ensure DOM elements are rendered
    const timer = setTimeout(initPlayers, 100);
    
    return () => {
      clearTimeout(timer);
      // Cleanup players on unmount
      playersRef.current.forEach((player) => {
        try {
          player.destroy();
        } catch (error) {
          console.warn('Error destroying player:', error);
        }
      });
      playersRef.current = [];
    };
  }, []);

  // Handle scrolling to section on page load if hash is present
  useEffect(() => {
    if (window.location.hash === '#testimonials') {
      setTimeout(() => {
        const testimonialsSection = document.getElementById('testimonials');
        if (testimonialsSection) {
          testimonialsSection.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100); // Small delay to ensure page is rendered
    }
  }, []);

  return (
    <div id="testimonials" className="youtube-testimonial-parent parent">
      <div className="youtube-testimonial-container container">
        {/* Header Section - Centered */}
        <div className="youtube-testimonial-header">
          <h2 className="youtube-testimonial-heading">
            Trusted by Patients. Backed by Doctors. <span>See What They're Saying.</span>
          </h2>
        </div>

        {/* Carousel Section */}
        <div className="youtube-testimonial-carousel-wrapper">
          <div className="youtube-embla">
            {/* Previous Button */}
            <button
              className="youtube-embla__prev"
              onClick={scrollPrev}
              disabled={prevBtnDisabled}
              aria-label="Previous testimonial"
            >
              <svg className="youtube-embla__button__svg" viewBox="0 0 532 532">
                <path
                  fill="currentColor"
                  d="M355.66 11.354c13.793-13.805 36.208-13.805 50.001 0 13.785 13.804 13.785 36.238 0 50.034L201.22 266l204.442 204.61c13.785 13.805 13.785 36.239 0 50.044-13.793 13.796-36.208 13.796-50.002 0a5994246.277 5994246.277 0 0 0-229.332-229.454 35.065 35.065 0 0 1-10.326-25.126c0-9.2 3.393-18.26 10.326-25.2C172.192 194.973 332.731 34.31 355.66 11.354Z"
                />
              </svg>
            </button>

            {/* Carousel Viewport */}
            <div className="youtube-embla__viewport" ref={emblaRef}>
              <div className="youtube-embla__container">
                {testimonials.map((testimonial) => (
                  <div className="youtube-embla__slide" key={testimonial.id}>
                    <div className="youtube-video-container">
                      <div className="youtube-video-wrapper">
                        {/* Placeholder div that YouTube IFrame API will replace */}
                        <div
                          id={`youtube-player-${testimonial.id}`}
                          className="youtube-iframe"
                          data-video-id={testimonial.videoId}
                          aria-label={testimonial.title}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Next Button */}
            <button
              className="youtube-embla__next"
              onClick={scrollNext}
              disabled={nextBtnDisabled}
              aria-label="Next testimonial"
            >
              <svg className="youtube-embla__button__svg" viewBox="0 0 532 532">
                <path
                  fill="currentColor"
                  d="M176.34 520.646c-13.793 13.805-36.208 13.805-50.001 0-13.785-13.804-13.785-36.238 0-50.034L330.78 266 126.34 61.391c-13.785-13.805-13.785-36.239 0-50.044 13.793-13.796 36.208-13.796 50.002 0 22.928 22.947 206.395 206.507 229.332 229.454a35.065 35.065 0 0 1 10.326 25.126c0 9.2-3.393 18.26-10.326 25.2C359.808 337.027 199.269 497.69 176.34 520.646Z"
                />
              </svg>
            </button>
          </div>

          {/* Navigation Instructions */}
          <div className="youtube-navigation-hint">
            <p className="youtube-nav-text">
              <span className="youtube-nav-desktop">Use the arrows to see more testimonials</span>
              <span className="youtube-nav-mobile">Swipe to see more testimonials</span>
            </p>
          </div>
        </div>

        {/* Bottom Section - Description and CTA */}
        <div className="youtube-testimonial-bottom">
          <p className="youtube-testimonial-description">
            Ready to experience the difference? Join thousands of satisfied patients.
          </p>
          <a
            href="/contact"
            className="youtube-testimonial-cta"
            aria-label="Get started with AxonCare today"
          >
            Get Started Today
          </a>
        </div>
      </div>
    </div>
  );
};

export default YouTubeTestimonials; 